import type { Logger } from '@/types/Logger';
import type { HeliaNode } from '@/types/poc/helia';
import type { AppServices, PocServices } from '@/types/services.types';
import type { AppDispatch, RootState } from '@/store';
import type { ThunkRejectedPayload } from '@/store/thunks/thunkErrorTypes';
import type { MLKEMKeyPair } from '@/services/abstract/poc/crypto.service.interface';
import type { UserFileKeyRef, FileKeyToRotate } from '@/types/fileKey/FileKeyRegeneration';
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { deriveUserKeyMaterial } from '@/store/thunks/auth/deriveUserKeyMaterial';
import { ensureDecapsPrivateKey } from '@/store/thunks/auth/ensureDecapsPrivateKey';
import { setDecapsKey } from '@/store/slices/auth/authSlice';
import { persistUkek } from '@/store/thunks/auth/persistUkek';
import { selectChangePasswordStatus } from '@/store/selectors/changePassword';
import { selectCurrentUserId, selectCurrentUsername } from '@/store/selectors/auth';
import {
  changePasswordCompleted,
  changePasswordFailed,
  changePasswordStarted,
  confirmationRequired,
  fileKeyRegenerated,
  fileKeyRegenerationFailed,
  phaseChanged,
  regenerationStarted,
  type ChangePasswordStage,
} from '@/store/slices/changePassword/changePasswordSlice';
import { FileEncryptionService } from '@/services/abstract/poc/fileEncryption.service.interface';
import { storeRecipientKeyBlob } from '@/store/thunks/fileKey/storeRecipientKeyBlob';
import { normalizeCid } from '@/services/poc/cidUtils';
import { bytesToHex } from '@/utils/hex.utils';
import { packTar } from '@/utils/tar/packTar';

const KEY_FETCH_TIMEOUT_MS = 30_000;
const TAR_FILE_NAME = 'file-keys.tar';
const TAR_MIME_TYPE = 'application/x-tar';

/** Elapsed milliseconds since `startedAt` (a `performance.now()` reading), rounded. */
function elapsedMs(startedAt: number): number {
  return Math.round(performance.now() - startedAt);
}

interface NewKeyMaterial {
  ukekHex: string;
  keyPair: MLKEMKeyPair;
  encryptedPrivateKeyHex: string;
}

/** A regenerated key blob, kept in memory so it can be both stored and tarred. */
interface RegeneratedEntry {
  fileId: string;
  newFileKeyCid: string;
  blobBytes: Uint8Array;
}

/** One rotated fileId→CID pair, carried into the commit step. */
interface RotatedKeyRef {
  fileId: string;
  newFileKeyCid: string;
}

/** The recoverable pre-commit work, carried into the commit + notify steps. */
interface PreparedRotation {
  tarCid: string;
  entries: RotatedKeyRef[];
  newMaterial: NewKeyMaterial;
}

/**
 * Authenticated change-password workflow (soft key rotation).
 *
 * Because the user's ML-KEM keypair is derived from their password, changing it
 * invalidates every file key wrapped to the old public key. This thunk re-wraps
 * all of them to the new keypair, bundles the regenerated blobs into an
 * uncompressed tar, stores everything in local Helia, and only THEN commits the
 * new OPAQUE password — so a mid-flow failure leaves the old password working.
 *
 * Ordering (matches the "don't navigate away or the password won't be set" copy):
 *  1. capture old decaps private key + derive new key material
 *  2. re-wrap each file key to the new public key, store new blob in Helia.
 *     If any key fails, the flow pauses for the user's explicit confirmation
 *     (`acceptFailedKeys`) — `finalizeKeyRotation` purges every key not listed,
 *     so proceeding with failures permanently drops access to those files. If
 *     ALL keys fail, the flow aborts outright.
 *  3. tar the regenerated blobs, store the tar in Helia
 *  4. hand the tar CID to HAP (`uploadRotatedFileKeys`); the fetcher polls that
 *     command to a terminal status, which is the confirmation HAP untarred it
 *  5. only then commit, in two calls: `credentials/rotate/init` for the OPAQUE
 *     OPRF round-trip, then `finalizeKeyRotation` — one Fabric-backed command
 *     carrying the OPAQUE record, the fileId→CID list, and the new ML-KEM keys
 */
export const changePasswordThunk = createAppAsyncThunk<
  undefined,
  { password: string; acceptFailedKeys?: boolean }
>(
  'changePassword/run',
  async ({ password, acceptFailedKeys = false }, thunkAPI) => {
    const { dispatch, getState, extra, rejectWithValue } = thunkAPI;
    const log = extra.loggerService.createLogger({ namespace: 'six3ro:change-password' });

    if (selectChangePasswordStatus(getState()) === 'running') {
      log.warn('Aborted: a change-password run is already in flight');
      return rejectWithValue({ message: 'A password change is already in progress.' });
    }

    const session = resolveSession(getState());
    if (session === null) {
      log.warn('Aborted: no signed-in session (missing username, userId, or UKEK)');
      return rejectWithValue({ message: 'You must be signed in to change your password.' });
    }
    const helia = extra.pocServices.p2pStackManagerService.getHeliaNode();
    if (helia === null) {
      log.warn('Aborted: Helia node not ready');
      return rejectWithValue({ message: 'The secure network is not ready yet. Please try again.' });
    }

    const startedAt = performance.now();
    log.info('Change password started', { userId: session.userId, username: session.username });
    dispatch(changePasswordStarted());

    // Steps 1-3: regenerate + tar + store. Recoverable — a failure here leaves
    // the old password working.
    // eslint-disable-next-line @typescript-eslint/init-declarations -- assigned in the try below; a throw short-circuits to the catch
    let prepared: PreparedRotation;
    try {
      prepared = await runPreCommit({
        password,
        acceptFailedKeys,
        session,
        helia,
        extra,
        dispatch,
        log,
      });
    } catch (error) {
      if (error instanceof RegenerationIncompleteError) {
        log.warn(
          `Pausing for confirmation: ${error.failedCount}/${error.totalCount} key(s) failed to regenerate; nothing committed`
        );
        dispatch(confirmationRequired());
        return rejectWithValue({ message: error.message });
      }
      return rejectWithValue(reportFailure(dispatch, log, error, undefined));
    }

    // Step 4: hand the tar off to HAP and poll the command to a terminal
    // status. Still recoverable — nothing is committed server-side yet.
    dispatch(phaseChanged('submitting'));
    log.info('Step 4/5 submitting: handing tar CID to HAP and polling the command', {
      tarCid: prepared.tarCid,
    });
    const submitStartedAt = performance.now();
    try {
      await extra.fileKeyRegenerationService.uploadRotatedFileKeys({
        compressedUpdatedFileKeysCID: prepared.tarCid,
      });
    } catch (error) {
      return rejectWithValue(reportFailure(dispatch, log, error, undefined));
    }
    log.info(`Step 4/5 done: HAP confirmed the tar in ${elapsedMs(submitStartedAt)}ms`);

    // Step 5: only now commit. `finalizeKeyRotation` is the single mutating
    // call, and a failure inside it is ambiguous (postCommit) — see below.
    dispatch(phaseChanged('committing'));
    log.info('Step 5/5 committing: OPAQUE rotate/init + rotate/finalize', {
      fileKeysToRotate: prepared.entries.length,
    });
    const commitStartedAt = performance.now();
    try {
      await commitNewPassword({ password, session, prepared, extra, dispatch, log });
    } catch (error) {
      const stage: ChangePasswordStage | undefined =
        error instanceof PostCommitError ? 'postCommit' : undefined;
      return rejectWithValue(reportFailure(dispatch, log, error, stage));
    }
    log.info(`Step 5/5 done: rotation committed in ${elapsedMs(commitStartedAt)}ms`);

    dispatch(changePasswordCompleted());
    log.info(`Change password completed in ${elapsedMs(startedAt)}ms`, {
      tarCid: prepared.tarCid,
      regeneratedCount: prepared.entries.length,
      failedCount: getState().changePassword.failedFileIds.length,
    });
  }
);

interface ResolvedSession {
  username: string;
  userId: string;
  oldUkekHex: string;
  oldDecapsKeyHex: string;
}

function resolveSession(state: RootState): ResolvedSession | null {
  const username = selectCurrentUsername(state).toLowerCase();
  const userId = selectCurrentUserId(state) ?? null;
  if (username === '' || userId === null || state.auth.ukek === '') {
    return null;
  }
  return {
    username,
    userId,
    oldUkekHex: state.auth.ukek,
    oldDecapsKeyHex: state.auth.decapsKey,
  };
}

interface PreCommitArgs {
  password: string;
  acceptFailedKeys: boolean;
  session: ResolvedSession;
  helia: HeliaNode;
  extra: AppServices;
  dispatch: AppDispatch;
  log: Logger;
}

async function runPreCommit(args: PreCommitArgs): Promise<PreparedRotation> {
  const { password, acceptFailedKeys, session, helia, extra, dispatch, log } = args;
  const { userService, fileKeyRegenerationService, pocServices } = extra;

  log.info('Step 1/5 preparing: unwrapping current ML-KEM private key');
  const oldPrivateKey = await ensureDecapsPrivateKey({
    decapsKeyHex: session.oldDecapsKeyHex,
    ukekHex: session.oldUkekHex,
    userId: session.userId,
    dispatch,
    userService,
    cryptoService: pocServices.cryptoService,
    log,
  });

  log.info('Step 1/5 preparing: deriving new key material from the new password');
  const { ukekHex, keyPair, encryptedPrivateKeyHex } = await deriveUserKeyMaterial(
    pocServices.cryptoService,
    session.username,
    password
  );
  const newMaterial: NewKeyMaterial = { ukekHex, keyPair, encryptedPrivateKeyHex };

  log.info('Step 2/5 regenerating: fetching file-key list (GET sixes/file-keys)');
  const fileKeys = await fileKeyRegenerationService.getUserFileKeys();
  log.info(`Step 2/5 regenerating: ${fileKeys.length} file key(s) to re-wrap`);
  log.debug('File keys to rotate', fileKeys);
  dispatch(regenerationStarted({ totalCount: fileKeys.length }));

  const regenerationStartedAt = performance.now();
  const entries = await regenerateAllFileKeys({
    fileKeys,
    userId: session.userId,
    oldPrivateKey,
    newPublicKey: keyPair.publicKey,
    helia,
    pocServices,
    dispatch,
    log,
  });

  log.info(
    `Step 2/5 done: ${entries.length}/${fileKeys.length} key(s) regenerated in ${elapsedMs(regenerationStartedAt)}ms`
  );

  // Gate before anything irreversible: `finalizeKeyRotation` purges every key
  // not listed in `fileKeysToRotate`, so a missing entry means losing that
  // file. Total failure aborts; partial failure needs the user's say-so.
  const failedCount = fileKeys.length - entries.length;
  if (fileKeys.length > 0 && entries.length === 0) {
    throw new Error('None of your file keys could be regenerated. Please try again later.');
  }
  if (failedCount > 0 && !acceptFailedKeys) {
    throw new RegenerationIncompleteError(failedCount, fileKeys.length);
  }

  dispatch(phaseChanged('bundling'));
  log.info(`Step 3/5 bundling: packing ${entries.length} key blob(s) into ${TAR_FILE_NAME}`);
  const tarBytes = await packTar(
    entries.map((entry) => ({ name: `${entry.fileId}.key.json`, bytes: entry.blobBytes }))
  );
  log.info(`Step 3/5 bundling: tar is ${tarBytes.byteLength} bytes`);

  dispatch(phaseChanged('uploading'));
  log.info('Step 3/5 uploading: storing tar in local Helia');
  const tarFile = new File([tarBytes], TAR_FILE_NAME, { type: TAR_MIME_TYPE });
  const tarResult = await pocServices.fileStoreService.addFile(helia, tarFile);
  const tarCid = normalizeCid(tarResult.cid);
  log.info('Step 3/5 done: tar stored in Helia', { tarCid });

  // Only the fileId→CID pairs survive past this point — the blob byte arrays
  // are dropped so they can be GC'd during the long upload/commit polling.
  return {
    tarCid,
    entries: entries.map(({ fileId, newFileKeyCid }) => ({ fileId, newFileKeyCid })),
    newMaterial,
  };
}

interface CommitArgs {
  password: string;
  session: ResolvedSession;
  prepared: PreparedRotation;
  extra: AppServices;
  dispatch: AppDispatch;
  log: Logger;
}

/**
 * Step 5: the commit, in two calls because the OPAQUE ceremony needs two server
 * round-trips. `credentials/rotate/init` runs here rather than earlier — the
 * OPAQUE session expires, and regeneration plus the tar transfer can be slow.
 */
async function commitNewPassword(args: CommitArgs): Promise<void> {
  const { password, session, prepared, extra, dispatch, log } = args;
  const { keyPair, encryptedPrivateKeyHex, ukekHex } = prepared.newMaterial;

  // 5a: OPRF round-trip only. This writes an OPAQUE session row that
  // `finalizeKeyRotation` consumes; nothing the user depends on changes yet.
  log.info('Step 5a: OPAQUE registration ceremony (POST credentials/rotate/init)');
  const { sessionId, registrationRecord } = await extra.authService.prepareRegistrationRecord({
    username: session.username,
    password,
    init: async (registrationRequest) =>
      await extra.authService.rotateCredentialInit({ registrationRequest }),
  });
  log.info('Step 5a done: OPAQUE session opened');

  const fileKeysToRotate: FileKeyToRotate[] = prepared.entries.map((entry) => ({
    fileId: entry.fileId,
    newFileKeyCID: entry.newFileKeyCid,
  }));

  // 5b: the commit point. Replaces both the standalone `finalizeRegistration`
  // POST and the `updateMlkemKeys` PATCH for this flow. A failure here is
  // ambiguous — the command may already be enqueued server-side — so it counts
  // as postCommit rather than recoverable.
  log.info('Step 5b: committing rotation (POST credentials/rotate/finalize) and polling', {
    fileKeysToRotate: fileKeysToRotate.length,
  });
  log.debug('fileKeysToRotate', fileKeysToRotate);
  try {
    await extra.fileKeyRegenerationService.finalizeKeyRotation({
      sessionId,
      registrationRecord,
      encapsKey: JSON.stringify(Array.from(keyPair.publicKey)),
      decapsKey: encryptedPrivateKeyHex,
      fileKeysToRotate,
    });
  } catch (error) {
    throw new PostCommitError(error);
  }

  log.info('Step 5b done: rotation command reached a terminal success; updating local key material');
  persistUkek(dispatch, session.userId, ukekHex);
  dispatch(setDecapsKey(bytesToHex(keyPair.privateKey)));
}

/** Marks an error that leaves it unknown whether the rotation committed. */
class PostCommitError extends Error {
  constructor(readonly cause: unknown) {
    super(cause instanceof Error ? cause.message : 'Post-commit failure');
    this.name = 'PostCommitError';
  }
}

/** Regeneration partially failed; the flow paused for the user's explicit go-ahead. */
class RegenerationIncompleteError extends Error {
  constructor(
    readonly failedCount: number,
    readonly totalCount: number
  ) {
    super(`${String(failedCount)} of ${String(totalCount)} file key(s) could not be regenerated.`);
    this.name = 'RegenerationIncompleteError';
  }
}

interface RegenerateAllArgs {
  fileKeys: UserFileKeyRef[];
  userId: string;
  oldPrivateKey: Uint8Array;
  newPublicKey: Uint8Array;
  helia: HeliaNode;
  pocServices: PocServices;
  dispatch: AppDispatch;
  log: Logger;
}

async function regenerateAllFileKeys(args: RegenerateAllArgs): Promise<RegeneratedEntry[]> {
  const entries: RegeneratedEntry[] = [];
  const totalCount = args.fileKeys.length;
  for (const [index, ref] of args.fileKeys.entries()) {
    const position = `${index + 1}/${totalCount}`;
    try {
      const entry = await regenerateOneFileKey(ref, args);
      entries.push(entry);
      args.log.info(`Regenerated key ${position} for file ${ref.fileId}`, {
        oldFileKeyCid: ref.fileKey,
        newFileKeyCid: entry.newFileKeyCid,
      });
      args.dispatch(fileKeyRegenerated());
    } catch (error) {
      args.log.error(
        `Failed to regenerate key ${position} for file ${ref.fileId}: ${error instanceof Error ? error.message : 'unknown error'}`,
        { oldFileKeyCid: ref.fileKey }
      );
      args.dispatch(fileKeyRegenerationFailed(ref.fileId));
    }
  }
  return entries;
}

async function regenerateOneFileKey(
  ref: UserFileKeyRef,
  args: RegenerateAllArgs
): Promise<RegeneratedEntry> {
  const { fileEncryptionService, fileStoreService } = args.pocServices;

  args.log.debug(`Fetching key blob for file ${ref.fileId}`, { fileKeyCid: ref.fileKey });
  const blobJsonBytes = await fileStoreService.getFile(args.helia, ref.fileKey, {
    signal: AbortSignal.timeout(KEY_FETCH_TIMEOUT_MS),
  });
  args.log.debug(`Fetched key blob for file ${ref.fileId} (${blobJsonBytes.byteLength} bytes)`);
  const currentEncryption = FileEncryptionService.parseRecipientKeyBlob(
    new TextDecoder().decode(new Uint8Array(blobJsonBytes)),
    args.userId
  );

  const reEncrypted = await fileEncryptionService.reEncryptFileKeyForRecipient(
    currentEncryption,
    args.oldPrivateKey,
    args.newPublicKey
  );

  const blobBytes = new TextEncoder().encode(
    FileEncryptionService.serializeRecipientKeyBlob(reEncrypted)
  );
  const newFileKeyCid = await storeRecipientKeyBlob(
    args.helia,
    fileStoreService,
    `${ref.fileId}.key.json`,
    blobBytes
  );

  return { fileId: ref.fileId, newFileKeyCid, blobBytes };
}

function reportFailure(
  dispatch: AppDispatch,
  log: Logger,
  error: unknown,
  stage: ChangePasswordStage | undefined
): ThunkRejectedPayload {
  const message =
    stage === 'postCommit'
      ? 'We could not confirm your password change finished. Try signing in with your new password — if that fails, your old one still works. Please contact support.'
      : error instanceof Error
        ? `${error.message} Your password was not changed.`
        : 'Changing your password failed. Your password was not changed. Please try again.';
  log.error(`Change password failed${stage !== undefined ? ` (${stage})` : ''}`, error);
  dispatch(changePasswordFailed({ message, stage }));
  return { message, stage };
}
